/**
 * Header for a tracker board, shared by every board engine so the briefs board
 * and the item boards cannot drift apart.
 *
 * The 56px shell has no description row, so the board's description lives in the
 * About dialog behind the info button, which only appears when there is one.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@monksflow/monks-ui';
import { EyeIcon } from 'lucide-react';
import { TrackerIconTile, useUpdateTrackerMutation, type Tracker } from '@/entities/tracker';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import { track } from '@/shared/analytics';
import { StaggerItem } from '@/shared/ui/animations';
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderEditableName,
  PageHeaderIdentity,
} from '@/shared/ui/layout';
import { AboutPageDialog } from '@/shared/ui';
import { ROUTES } from '@/shared/config/routes';
import { AutomationsButton, boardTypeOf, useAutomationLiveResults } from '@/widgets/trackers-board';
import { trackersPageTitle } from './constants/trackers.constants';
import { TrackerDetailsMenu } from './TrackerDetailsMenu';
import { TrackerShareButton } from './TrackerShareButton';
import { useTrackerAccess } from './hooks/useTrackerAccess';

interface TrackerBoardHeaderProps {
  tracker: Tracker;
  /** What the board holds, where the tracker itself does not say. */
  description: string | null;
}

export function TrackerBoardHeader({ tracker, description }: TrackerBoardHeaderProps) {
  const { t } = useTranslation('trackers');

  const workspaceId = useCurrentWorkspaceId() ?? '';
  const updateTracker = useUpdateTrackerMutation({ workspaceId });
  const [aboutOpen, setAboutOpen] = useState(false);
  const { canEdit, canManage } = useTrackerAccess(tracker);
  // Every viewer hears the board's results, whether or not they may edit it.
  useAutomationLiveResults(tracker);

  const handleRename = (name: string) => {
    updateTracker.mutate(
      { trackerId: tracker.id, body: { name } },
      {
        onSuccess: () => {
          track('tracker_renamed', {
            tracker_id: tracker.id,
            board_type: boardTypeOf(tracker.itemSource),
          });
        },
        // The crumb drops its draft the moment it commits, so a rejected PATCH
        // leaves the old name on screen with nothing to say it was rejected.
        onError: () =>
          notificationService.showError(t('trackers.trackerBoard.couldNotRenameTheTracker')),
      },
    );
  };

  return (
    <>
      <StaggerItem index={0}>
        <PageHeader>
          <PageHeaderIdentity>
            <PageHeaderBreadcrumb
              info={
                description ? { page: tracker.name, onClick: () => setAboutOpen(true) } : undefined
              }
            >
              <PageHeaderBreadcrumbLink href={ROUTES.TRACKERS}>
                {trackersPageTitle()}
              </PageHeaderBreadcrumbLink>
              <span className="flex min-w-0 items-center gap-xs">
                <TrackerIconTile
                  icon={tracker.icon}
                  hue={tracker.hue}
                  size="sm"
                  data-testid="tracker-header-icon"
                />
                {canManage ? (
                  <PageHeaderEditableName
                    value={tracker.name}
                    label={t('trackers.trackerBoard.trackerName')}
                    onCommit={handleRename}
                  />
                ) : (
                  <PageHeaderBreadcrumbPage>{tracker.name}</PageHeaderBreadcrumbPage>
                )}
              </span>
            </PageHeaderBreadcrumb>
          </PageHeaderIdentity>
          <PageHeaderActions>
            {canEdit ? null : (
              <Badge variant="outline" roundness="full">
                <EyeIcon aria-hidden />
                {t('trackers.viewOnly.badge')}
              </Badge>
            )}
            <AutomationsButton tracker={tracker} size="lg" />
            {canManage ? <TrackerShareButton tracker={tracker} size="lg" /> : null}
            <TrackerDetailsMenu tracker={tracker} size="lg" />
          </PageHeaderActions>
        </PageHeader>
      </StaggerItem>

      <AboutPageDialog
        open={aboutOpen}
        onOpenChange={setAboutOpen}
        title={t('trackers.trackerBoard.aboutThisTracker')}
        description={description ?? undefined}
        rows={[]}
      />
    </>
  );
}
